// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * DatasetsPage — agent-owned dataset management for the current workspace.
 *
 * Every dataset belongs to exactly one agent. Workspace members get a
 * view-only table; global admins get full CRUD and clone (into any agent in
 * the workspace).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Plus, Pencil, Trash2, Eye, Copy } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TableCard, TableCardEmpty, TableCardLoading } from '@/components/ui/table-card'
import { Badge } from '@/components/ui/badge'
import { ConfigFlowStrip } from '@/app-shell/components/ConfigFlowStrip'
import { ConfirmDialog } from '@/components/forms'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useServices } from '@/app-shell/services'
import { useAuth } from '@/subsystems/workspaces'
import { useAgents } from '@/subsystems/agents'
import { useDatasets } from '@/subsystems/pipelines'
import {
  DatasetEditDialog,
  type DatasetEditData,
} from '@/subsystems/pipelines/components/DatasetEditDialog'
import type { DatasetRecord } from '@/subsystems/pipelines/types'
import {
  upsertDataset,
  deleteDataset,
  cloneDataset,
} from '@/subsystems/pipelines/services/dataset.service'

interface CloneTarget {
  agentId: string
  key: string
}

const DatasetsPage: FC = () => {
  const services = useServices()
  const apiClient = services.getClient()
  const { selectedOrganizationId, selectedWorkspaceId, isGlobalAdmin } = useAuth()
  const orgId = selectedOrganizationId ?? ''
  const wsId = selectedWorkspaceId ?? ''
  const { datasets, refresh, loading } = useDatasets(orgId, wsId)
  const { data: agents = [] } = useAgents(apiClient, orgId, wsId)

  const agentLabel = useCallback(
    (agentId: string) => agents.find(a => a.agentId === agentId)?.label ?? agentId,
    [agents],
  )

  // The agent Prompts tab links here with ?agent=<id> to pre-filter to one agent.
  const [searchParams] = useSearchParams()
  const [agentFilter, setAgentFilter] = useState<string>(searchParams.get('agent') ?? 'all')

  const filtered = useMemo(() => {
    let result = [...datasets].sort((a, b) => a.label.localeCompare(b.label))
    if (agentFilter !== 'all') {
      result = result.filter((d) => d.agentId === agentFilter)
    }
    return result
  }, [datasets, agentFilter])

  // Edit / view / delete state
  const [editOpen, setEditOpen] = useState(false)
  const [editingDataset, setEditingDataset] = useState<DatasetRecord | null>(null)
  // The agent that owns the dataset being edited (existing row) or created (picked).
  const [editingAgentId, setEditingAgentId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [viewOpen, setViewOpen] = useState(false)
  const [viewingDataset, setViewingDataset] = useState<DatasetRecord | null>(null)

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deletingDataset, setDeletingDataset] = useState<DatasetRecord | null>(null)

  // New dataset agent picker / clone target picker
  const [newAgentPickerOpen, setNewAgentPickerOpen] = useState(false)
  const [clonePickerOpen, setClonePickerOpen] = useState(false)
  const [cloneSource, setCloneSource] = useState<DatasetRecord | null>(null)

  const handleView = (dataset: DatasetRecord) => {
    setViewingDataset(dataset)
    setViewOpen(true)
  }

  const handleNewClick = () => {
    setNewAgentPickerOpen(true)
  }

  const handleNewAgentChosen = (agentId: string) => {
    setNewAgentPickerOpen(false)
    setEditingDataset(null)
    setEditingAgentId(agentId)
    setEditOpen(true)
  }

  const handleEdit = (dataset: DatasetRecord) => {
    setEditingDataset(dataset)
    setEditingAgentId(dataset.agentId)
    setEditOpen(true)
  }

  // The agent Prompts tab links here with ?agent=<id>&dataset=<key> to jump
  // straight into a dataset's editor. Open it once after the datasets load.
  const autoOpenedRef = useRef(false)
  useEffect(() => {
    if (autoOpenedRef.current || loading || !isGlobalAdmin) return
    const agent = searchParams.get('agent')
    const key = searchParams.get('dataset')
    if (!agent || !key) return
    const match = datasets.find(d => d.agentId === agent && d.key === key)
    if (!match) return
    autoOpenedRef.current = true
    setEditingDataset(match)
    setEditingAgentId(match.agentId)
    setEditOpen(true)
  }, [datasets, loading, isGlobalAdmin, searchParams])

  const handleSave = useCallback(
    async (data: DatasetEditData) => {
      if (!editingAgentId) return
      setSaving(true)
      try {
        await upsertDataset(apiClient, orgId, wsId, editingAgentId, data.key, {
          label: data.label,
          description: data.description,
          schemaId: data.schemaId,
          knowledgeBase: data.knowledgeBase,
          systemPrompt: data.systemPrompt,
          knowledgeBaseBatchSize: data.knowledgeBaseBatchSize,
          pageMode: data.pageMode,
          diffMode: data.diffMode,
        })
        setEditOpen(false)
        await refresh()
        toast.success(`Dataset "${data.label}" saved`)
      } catch {
        toast.error('Failed to save dataset')
      } finally {
        setSaving(false)
      }
    },
    [apiClient, editingAgentId, orgId, wsId, refresh],
  )

  const startClone = (dataset: DatasetRecord) => {
    setCloneSource(dataset)
    setClonePickerOpen(true)
  }

  const handleCloneConfirm = useCallback(
    async (target: CloneTarget) => {
      if (!cloneSource) return
      setClonePickerOpen(false)
      try {
        await cloneDataset(
          apiClient, orgId, wsId,
          cloneSource.agentId, cloneSource.key,
          target.agentId, target.key,
        )
        await refresh()
        toast.success(`Dataset cloned as "${target.key}"`)
      } catch {
        toast.error('Failed to clone dataset')
      }
      setCloneSource(null)
    },
    [apiClient, cloneSource, orgId, wsId, refresh],
  )

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingDataset) return
    try {
      await deleteDataset(apiClient, orgId, wsId, deletingDataset.agentId, deletingDataset.key)
      setDeleteConfirmOpen(false)
      await refresh()
      toast.success('Dataset deleted')
    } catch {
      toast.error('Failed to delete dataset')
    }
  }, [apiClient, deletingDataset, orgId, wsId, refresh])

  if (!orgId || !wsId) {
    return (
      <div className="min-h-full bg-slate-50">
        <div className="mx-auto max-w-[1280px] px-6 pt-9 sm:px-10 text-sm text-slate-500">
          Select a workspace to view datasets.
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-full bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-[1280px] px-6 pt-9 pb-16 sm:px-10">
      <h1 className="text-[21px] font-[650] tracking-[-0.015em]">Datasets</h1>
      <p className="mt-[5px] text-[13px] text-slate-500">
        Each dataset tells an agent what to produce: the schema it fills and, optionally, the
        knowledge base it consults during review.
      </p>

      {isGlobalAdmin && (
        <div className="mt-6">
          <ConfigFlowStrip current="datasets" />
        </div>
      )}

      <div className="mt-6 mb-4 flex flex-wrap items-center gap-2.5">
        <div className="w-56">
          <Select value={agentFilter} onValueChange={setAgentFilter}>
            <SelectTrigger className="bg-white" aria-label="Filter by agent">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All agents</SelectItem>
              {agents.map((a) => (
                <SelectItem key={a.agentId} value={a.agentId}>
                  {a.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span className="flex-1" />
        {isGlobalAdmin && (
          <Button size="sm" onClick={handleNewClick} data-testid="datasets-new">
            <Plus className="h-4 w-4 mr-1" />
            New Dataset
          </Button>
        )}
      </div>

      <TableCard
        title="Workspace datasets"
        meta={
          filtered.length > 0
            ? `${filtered.length} dataset${filtered.length === 1 ? '' : 's'}`
            : undefined
        }
      >
        {loading ? (
          <TableCardLoading label="Loading datasets…" />
        ) : filtered.length === 0 ? (
          datasets.length === 0 ? (
            <TableCardEmpty
              title="No datasets yet"
              description="Create a dataset to define what an agent produces when it analyzes documents — pick the schema it fills and, optionally, a knowledge base to consult."
              action={
                isGlobalAdmin && (
                  <Button size="sm" onClick={handleNewClick}>
                    <Plus className="h-4 w-4 mr-1" />
                    New Dataset
                  </Button>
                )
              }
            />
          ) : (
            <TableCardEmpty
              title="No datasets for this agent"
              description="Switch the agent filter, or create a dataset for this agent."
            />
          )
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Key</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead>Schema</TableHead>
                <TableHead>Knowledge Base</TableHead>
                <TableHead className="w-28" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((ds) => (
                <TableRow
                  key={`${ds.agentId}#${ds.key}`}
                  data-testid={`dataset-row-${ds.agentId}-${ds.key}`}
                >
                  <TableCell className="font-mono text-xs">{ds.key}</TableCell>
                  <TableCell className="font-[550]">{ds.label}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {agentLabel(ds.agentId)}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">
                    {ds.schemaId ?? '—'}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">
                    {ds.knowledgeBase ?? '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => (isGlobalAdmin ? handleEdit(ds) : handleView(ds))}
                        title={isGlobalAdmin ? 'Edit' : 'View'}
                        aria-label={isGlobalAdmin ? 'Edit dataset' : 'View dataset'}
                      >
                        {isGlobalAdmin ? (
                          <Pencil className="h-3.5 w-3.5" />
                        ) : (
                          <Eye className="h-3.5 w-3.5" />
                        )}
                      </Button>
                      {isGlobalAdmin && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => startClone(ds)}
                            title="Clone"
                            aria-label="Clone dataset"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive"
                            onClick={() => {
                              setDeletingDataset(ds)
                              setDeleteConfirmOpen(true)
                            }}
                            title="Delete"
                            aria-label="Delete dataset"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableCard>

      <DatasetEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        dataset={editingDataset}
        onSave={handleSave}
        saving={saving}
        kbOrgId={orgId}
        kbWsId={wsId}
        kbPicker
      />
      <DatasetEditDialog
        open={viewOpen}
        onOpenChange={setViewOpen}
        dataset={viewingDataset}
        onSave={() => {}}
        readonly
        kbOrgId={orgId}
        kbWsId={wsId}
        kbPicker
      />
      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete Dataset"
        description={`Delete "${deletingDataset?.key}" from ${agentLabel(deletingDataset?.agentId ?? '')}? This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
      />

      {isGlobalAdmin && (
        <NewAgentPicker
          open={newAgentPickerOpen}
          onOpenChange={setNewAgentPickerOpen}
          agents={agents.map(a => ({ agentId: a.agentId, label: a.label }))}
          onChosen={handleNewAgentChosen}
        />
      )}

      {isGlobalAdmin && cloneSource && (
        <CloneTargetPicker
          open={clonePickerOpen}
          onOpenChange={setClonePickerOpen}
          source={cloneSource}
          sourceAgentLabel={agentLabel(cloneSource.agentId)}
          agents={agents.map(a => ({ agentId: a.agentId, label: a.label }))}
          onConfirm={handleCloneConfirm}
        />
      )}
      </div>
    </div>
  )
}

export default DatasetsPage

interface AgentOption {
  agentId: string
  label: string
}

interface NewAgentPickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  agents: AgentOption[]
  onChosen: (agentId: string) => void
}

const NewAgentPicker: FC<NewAgentPickerProps> = ({
  open,
  onOpenChange,
  agents,
  onChosen,
}) => {
  const [agentId, setAgentId] = useState<string>('')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Dataset</DialogTitle>
          <DialogDescription>Choose the agent that owns this dataset.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Agent</Label>
            <Select value={agentId} onValueChange={setAgentId}>
              <SelectTrigger data-testid="datasets-agent-select">
                <SelectValue placeholder="Select an agent..." />
              </SelectTrigger>
              <SelectContent>
                {agents.map((a) => (
                  <SelectItem key={a.agentId} value={a.agentId}>
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!agentId} onClick={() => onChosen(agentId)}>
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface CloneTargetPickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  source: DatasetRecord
  sourceAgentLabel: string
  agents: AgentOption[]
  onConfirm: (target: CloneTarget) => void
}

const CloneTargetPicker: FC<CloneTargetPickerProps> = ({
  open,
  onOpenChange,
  source,
  sourceAgentLabel,
  agents,
  onConfirm,
}) => {
  const [agentId, setAgentId] = useState<string>(source.agentId)
  const [key, setKey] = useState<string>(`${source.key}_copy`)

  const canConfirm = !!key && !!agentId

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Clone Dataset</DialogTitle>
          <DialogDescription>
            Cloning <code className="text-xs">{source.key}</code> from {sourceAgentLabel}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Target Agent</Label>
            <Select value={agentId} onValueChange={setAgentId}>
              <SelectTrigger>
                <SelectValue placeholder="Select an agent..." />
              </SelectTrigger>
              <SelectContent>
                {agents.map((a) => (
                  <SelectItem key={a.agentId} value={a.agentId}>
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>New Key</Label>
            <Input value={key} onChange={(e) => setKey(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!canConfirm}
            onClick={() => onConfirm({ agentId, key })}
          >
            Clone
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
